import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { debounceTime, distinctUntilChanged, startWith, Subject, take, takeUntil } from 'rxjs';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ManagementBusinessEntityControllerService, ManagementBusinessEntityResponse } from 'api-contract-notices';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-analytics-filters',
  standalone: true,
  imports: [
		MatCardModule,
		CommonModule,
		ReactiveFormsModule,
		FormsModule,
		MatFormFieldModule,
		TranslateModule,
		MatDatepickerModule,
		MatToolbarModule,
		MatIconModule,
		MatProgressSpinnerModule,
		MatButtonModule,
		MatDividerModule,
		MatTooltipModule,
		MatAutocompleteModule,
		MatOptionModule,
		MatInputModule,
	],
  templateUrl: './analytics-filters.component.html',
  styleUrl: './analytics-filters.component.scss'
})
export class AnalyticsFiltersComponent implements OnInit, OnChanges, OnDestroy {
  private onDestroy: Subject<boolean> = new Subject();

  @Input() role!: 'admin' | 'client' | 'landing';
  @Input() dataAdministrationType!: 'ANALYTICS';
  @Input() resetFilters: any;
  @Input() apiLoading!: boolean;
  @Output() actionRequested: EventEmitter<any> = new EventEmitter();

  readonly INSTANT_SEARCH = false; // setting option to have search without button
  filtersFormGroup: FormGroup = new FormGroup({});

  DAYS_RANGE = 0;
  DATE_RANGE_LIMITS = {
    dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
    dateTo: new Date(),
  };

  constructor(private fb: FormBuilder, public utils: UtilsService, private sharedService: SharedService) {}

  ngOnInit(): void {
    this.formInitializer();

    this.sharedService.requestActionInComponent.pipe(takeUntil(this.onDestroy)).subscribe({
      next: response => {
        if (response.componentName === 'AnalyticsFiltersComponent') {
          switch (response.action) {
            case 'FILTER_TRIGGER': {
              this.filterListItems();
              break;
            }
          }
        }
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resetFilters'] && !changes['resetFilters'].currentValue && !changes['resetFilters'].firstChange) {
      this.resetForm();
    }
    if (changes['dataAdministrationType'] && !changes['dataAdministrationType'].firstChange) {
      this.resetForm();
    }
    if (changes['role'] && changes['role'].currentValue === 'client') {
      this.DAYS_RANGE = 7;
      this.DATE_RANGE_LIMITS = {
        dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
        dateTo: new Date(),
      };
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next(true);
  }

  private formInitializer() {
    this.filtersFormGroup = this.fb.group({
      startDate: [this.DATE_RANGE_LIMITS.dateFrom],
      endDate: [this.DATE_RANGE_LIMITS.dateTo],
    });
  }

  resetForm(formDirective?: FormGroupDirective) {
    this.filtersFormGroup.reset();

    if (formDirective) {
      formDirective.resetForm();
    }

    this.filtersFormGroup.controls['startDate'].setValue(this.DATE_RANGE_LIMITS.dateFrom);
    this.filtersFormGroup.controls['endDate'].setValue(this.DATE_RANGE_LIMITS.dateTo);

    this.filterListItems();
  }

  filterListItems() {
    const filterDto = JSON.parse(JSON.stringify(this.filtersFormGroup.value));
    filterDto.startDate = this.utils.setDateToDay(this.filtersFormGroup.controls['startDate'].value, 'START');
    filterDto.endDate = this.utils.setDateToDay(this.filtersFormGroup.controls['endDate'].value, 'END');
    this.actionRequested.emit({ action: 'FILTER', filters: filterDto, id: 0 });
  }
}
